import { SEASON_SETTINGS_SAVE_DEBOUNCE_MS } from "@/lib/constants";
import type { Season } from "@/features/seasons/models/season";
import { normalizeSeason, toSeasonSettingsPayload } from "@/features/seasons/lib/season-settings";
import { seasonsQueryKeys } from "@/features/seasons/queries/seasons-query-keys";
import { useUpdateSeasonSettingsMutation } from "@/features/seasons/mutations/use-update-season-settings";
import { type InfiniteData, useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

type SeasonSettingsContextValue = {
  getDraft: (season: Season) => Season;
  updateDraft: (seasonId: string, patch: Partial<Season>) => void;
  isPending: (seasonId: string) => boolean;
};

const SeasonSettingsContext = createContext<SeasonSettingsContextValue | null>(null);

export function SeasonSettingsProvider({
  sport,
  organizationId,
  seasons,
  children,
}: {
  sport: string;
  organizationId: string;
  seasons: Season[];
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { mutate, isPending, variables } = useUpdateSeasonSettingsMutation();
  const [drafts, setDrafts] = useState<Record<string, Season>>({});
  const draftsRef = useRef(drafts);
  const timersRef = useRef<Record<string, number>>({});
  const versionsRef = useRef<Record<string, number>>({});

  draftsRef.current = drafts;

  useEffect(() => {
    setDrafts((current) => {
      let changed = false;
      const next = { ...current };

      for (const season of seasons) {
        if (!next[season.seasonId]) {
          next[season.seasonId] = normalizeSeason(season);
          changed = true;
        }
      }

      return changed ? next : current;
    });
  }, [seasons]);

  const persistSeason = useCallback(
    (seasonId: string, version: number) => {
      const draft = draftsRef.current[seasonId];
      if (!draft) {
        return;
      }

      const payload = toSeasonSettingsPayload(draft);

      mutate(
        { sport, organizationId, payload, version },
        {
          onSuccess: () => {
            if (versionsRef.current[seasonId] !== version) {
              return;
            }

            queryClient.setQueriesData<InfiniteData<Season[]>>({ queryKey: seasonsQueryKeys.all }, (current) => {
              if (!current?.pages) {
                return current;
              }

              return {
                ...current,
                pages: current.pages.map((page) =>
                  page.map((season) => (season.seasonId === payload.seasonId ? { ...season, ...payload } : season)),
                ),
              };
            });

            toast.success(t("seasons.list.saved"));
          },
          onError: (error) => {
            if (versionsRef.current[seasonId] !== version) {
              return;
            }

            const message = error instanceof Error ? error.message : t("seasons.list.save_failed");
            toast.error(message);
          },
        },
      );
    },
    [mutate, organizationId, queryClient, sport, t],
  );

  const updateDraft = useCallback(
    (seasonId: string, patch: Partial<Season>) => {
      setDrafts((current) => {
        const existing = current[seasonId];
        if (!existing) {
          return current;
        }

        return { ...current, [seasonId]: { ...existing, ...patch } };
      });

      versionsRef.current[seasonId] = (versionsRef.current[seasonId] ?? 0) + 1;
      const version = versionsRef.current[seasonId];

      window.clearTimeout(timersRef.current[seasonId]);
      timersRef.current[seasonId] = window.setTimeout(() => {
        persistSeason(seasonId, version);
      }, SEASON_SETTINGS_SAVE_DEBOUNCE_MS);
    },
    [persistSeason],
  );

  useEffect(() => {
    return () => {
      Object.values(timersRef.current).forEach((timer) => window.clearTimeout(timer));
    };
  }, []);

  const getDraft = useCallback(
    (season: Season) => drafts[season.seasonId] ?? normalizeSeason(season),
    [drafts],
  );

  const isSeasonPending = useCallback(
    (seasonId: string) => isPending && variables?.payload.seasonId === seasonId,
    [isPending, variables],
  );

  const value = useMemo(
    () => ({ getDraft, updateDraft, isPending: isSeasonPending }),
    [getDraft, isSeasonPending, updateDraft],
  );

  return <SeasonSettingsContext.Provider value={value}>{children}</SeasonSettingsContext.Provider>;
}

export function useSeasonSettings() {
  const context = useContext(SeasonSettingsContext);

  if (!context) {
    throw new Error("useSeasonSettings must be used within SeasonSettingsProvider");
  }

  return context;
}

export function useSeasonDraft(season: Season) {
  const { getDraft, updateDraft, isPending } = useSeasonSettings();
  const draft = getDraft(season);

  return {
    draft,
    isPending: isPending(season.seasonId),
    updateDraft: (patch: Partial<Season>) => updateDraft(season.seasonId, patch),
  };
}
